import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection, query, orderBy, limit, getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { sellersCollectionName, reviewsCollectionName } from "../services/mode";
import { compactNumber, relativeTime } from "../utils/format";
import "./ActivityStrip.css";

// Stats + recent reviews band that lives above the directory grid.
// Reviews are the proof of buyer activity (we don't have "buyer profiles"
// in the directory, so reviews are how the platform's pulse shows up).
export default function ActivityStrip() {
  const [stats, setStats] = useState({ sellers: 0, reviews: 0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Index-free queries: pure limit/orderBy, no WHERE clauses. We
        // filter active+visible client-side from the small sample.
        const sellersSnap = await getDocs(
            query(collection(db, sellersCollectionName()), limit(500)),
        );
        const revSnap = await getDocs(
            query(collection(db, reviewsCollectionName()),
                orderBy("createdAt", "desc"),
                limit(20)),
        );
        if (cancelled) return;
        const activeSellers = sellersSnap.docs.filter(
            (d) => d.data().status === "active" && !d.data().paused);
        const visibleReviews = revSnap.docs
            .filter((d) => !d.data().hidden)
            .slice(0, 5);
        setStats({
          sellers: activeSellers.length,
          reviews: activeSellers.reduce(
              (a, d) => a + (Number(d.data().reviewCount) || 0), 0),
        });
        setRecent(visibleReviews.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.warn("[pop-market] activity strip failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (stats.sellers === 0 && recent.length === 0) return null;

  return (
    <div className="act-strip">
      <div className="act-stats">
        <Stat label="ACTIVE SELLERS" value={compactNumber(stats.sellers)} />
        <Stat label="BUYER REVIEWS" value={compactNumber(stats.reviews)} />
        <Stat label="GAMES" value="2" sub="BGMI · PUBGM" />
        <Stat label="REGIONS" value="6+" sub="Global delivery" />
      </div>
      {recent.length > 0 && (
        <div className="act-feed">
          <div className="act-feed-label">RECENT BUYER REVIEWS</div>
          <div className="act-feed-rows">
            {recent.map((r) => (
              <Link
                key={r.id}
                to={`/seller/${r.sellerHandle}`}
                className="act-row"
              >
                <span className="act-stars">{"★".repeat(r.rating)}</span>
                <span className="act-text">
                  {r.text && r.text.length > 70 ? r.text.slice(0, 67) + "…" : r.text}
                </span>
                <span className="act-meta">
                  <span className="act-reviewer">
                    @{r.reviewerHandle || "anon"}
                  </span>
                  <span className="dim"> · {relativeTime(r.createdAt)}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="act-stat">
      <div className="act-stat-value">{value}</div>
      <div className="act-stat-label">{label}</div>
      {sub && <div className="act-stat-sub">{sub}</div>}
    </div>
  );
}
